import { useTheme } from "@emotion/react";
import { TextField as MuiTextField, type TextFieldProps as MuiTextFieldProps } from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider,
  type DatePickerProps,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import type { PickerValue } from "@mui/x-date-pickers/internals";
import { useState, type ReactNode } from "react";

import { FormFieldInformation } from "../FormFieldInformation";

import * as styles from "./DatePicker.styles";

type TextFieldProps = MuiTextFieldProps & {
  errors?: ReactNode[];
};

const TextField = ({ ref, ...props }: TextFieldProps) => {
  const { errors, ...rest } = props;

  return (
    <div css={styles.DatePickerWrapper}>
      <MuiTextField {...rest} ref={ref} />
      <FormFieldInformation errors={errors} />
    </div>
  );
};

interface Props extends Omit<DatePickerProps, "renderInput" | "onChange"> {
  label: string;
  variant: "dialog" | "inline";
  onChange: (date: Date) => void;
  isInTheFuture?: boolean;
  errors?: ReactNode[];
  required?: boolean;
}

/**
 * @deprecated Use `DatePicker` from `@fullscript/aviary-web` instead.
 */
const DatePicker = (props: Props) => {
  const { variant, isInTheFuture, errors, value, required, onChange, ...rest } = props;

  const currentTheme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const portalProps =
    variant === "inline"
      ? { PopperProps: { disablePortal: true } }
      : { DialogProps: { disablePortal: true } };

  const dateRangeProps = isInTheFuture ? { minDate: new Date() } : { maxDate: new Date() };

  const hasErrors = () => {
    if (!errors) return false;

    return errors.filter(e => !!e).length > 0;
  };

  const handleChange = (date: PickerValue) => {
    if (date) {
      onChange(date);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        {...{
          variant,
          ...portalProps,
          ...dateRangeProps,
        }}
        value={value ?? null}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        onChange={handleChange}
        views={["year", "month", "day"]}
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          toolbar: { hidden: true },
          textField: {
            required,
            error: hasErrors(),
            variant: "filled",
            id: "mui-theme-provider-standard-input",
            helperText: null,
            errors,
            sx: styles.textField(currentTheme),
            fullWidth: true,
            readOnly: isInTheFuture,
          } as any,
        }}
        slots={{
          textField: TextField,
        }}
        {...rest}
      />
    </LocalizationProvider>
  );
};

export { DatePicker, type Props };
